import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Debug function to check notifications for a specific user
export const debugUserNotifications = async (userId) => {
  try {
    console.log('🔍 Debugging notifications for user:', userId);
    
    // First, check ALL notifications in the database
    const allNotificationsQuery = query(collection(db, 'notifications'));
    const allNotificationsSnapshot = await getDocs(allNotificationsQuery);
    const allNotifications = allNotificationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📊 Total notifications in database: ${allNotifications.length}`);
    console.log('🗂️ All notifications by user:');
    const notificationsByUser = {};
    allNotifications.forEach(notif => {
      if (!notificationsByUser[notif.userId]) {
        notificationsByUser[notif.userId] = [];
      }
      notificationsByUser[notif.userId].push(notif);
    });
    
    Object.keys(notificationsByUser).forEach(uid => {
      console.log(`   User ${uid}: ${notificationsByUser[uid].length} notifications`);
    });
    
    // Get notifications for this specific user
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📊 Found ${notifications.length} notifications for user ${userId}:`);
    notifications.forEach((notif, index) => {
      console.log(`${index + 1}. Type: ${notif.type}, Title: ${notif.title}, Read: ${notif.read}`);
      console.log(`   Message: ${notif.message}`);
      console.log(`   UserId: ${notif.userId}`);
      console.log(`   Created: ${notif.createdAt?.toDate?.()?.toLocaleString() || 'Unknown'}`);
      console.log('   ---');
    });
    
    return notifications;
  } catch (error) {
    console.error('❌ Error debugging notifications:', error);
    return [];
  }
};

// Create a test assignment notification directly
export const createTestAssignmentNotification = async (userId, userName) => {
  try {
    console.log('🧪 Creating test assignment notification for:', userId, userName);
    
    const testNotification = {
      userId: userId,
      type: 'task_assignment',
      title: 'Test Task Assignment',
      message: `Test assignment notification for ${userName}`,
      taskId: 'test-task-123',
      taskName: 'Debug Test Task',
      activityId: 'test-activity-123',
      activityName: 'Debug Test Activity',
      projectId: 'test-project-123',
      projectName: 'Debug Test Project',
      assignedBy: 'Debug System',
      assignedByPhotoURL: '',
      createdAt: serverTimestamp(),
      read: false
    };
    
    const docRef = await addDoc(collection(db, 'notifications'), testNotification);
    console.log('✅ Test assignment notification created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating test assignment notification:', error);
    throw error;
  }
};

// Create a test mention notification directly
export const createTestMentionNotification = async (userId, userName) => {
  try {
    console.log('🧪 Creating test mention notification for:', userId, userName);
    
    const testNotification = {
      userId: userId,
      type: 'message_mention',
      title: 'Test Mention Notification',
      message: `Debug System mentioned you: "Testing @${userName} mention functionality"`,
      taskId: 'test-task-123',
      taskName: 'Debug Test Task',
      activityId: 'test-activity-123',
      activityName: 'Debug Test Activity',
      projectId: 'test-project-123',
      projectName: 'Debug Test Project',
      messageId: 'test-message-123',
      mentionedBy: 'Debug System',
      mentionedByPhotoURL: '',
      createdAt: serverTimestamp(),
      read: false
    };
    
    const docRef = await addDoc(collection(db, 'notifications'), testNotification);
    console.log('✅ Test mention notification created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating test mention notification:', error);
    throw error;
  }
};

// Debug function to check user data
export const debugUserData = async (userId) => {
  try {
    console.log('🔍 Debugging user data for:', userId);
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const users = [];
    
    snapshot.forEach(doc => {
      const userData = { id: doc.id, ...doc.data() };
      users.push(userData);
      if (doc.id === userId) {
        console.log('👤 Current user data:', userData);
        console.log(`   Role: ${userData.role || 'No role set'}`);
        console.log(`   Email: ${userData.email}`);
        console.log(`   Display Name: ${userData.displayName}`);
      }
    });
    
    console.log(`📊 Total users in database: ${users.length}`);
    console.log('👥 All users with roles:');
    users.forEach(u => {
      console.log(`   ${u.email} (${u.displayName || 'No name'}) - Role: ${u.role || 'No role'} - ID: ${u.id}`);
    });
    
    return users;
  } catch (error) {
    console.error('❌ Error debugging user data:', error);
    return [];
  }
};

export default {
  debugUserNotifications,
  createTestAssignmentNotification,
  createTestMentionNotification,
  debugUserData
};
